import { useState, useEffect } from 'react';
import { indexArray } from '../../../../../library/array/array';
import { TOASTR_TYPE_ERROR, TOASTR_TYPE_SUCCESS } from '../../../../../utils/toastr';
import { postData } from '../../../../../utils/async';
import { getMealCount } from '../../shared/utils/meals';
import { getOrdersProductData } from '../../shared/utils/ordersProductData';
import {
    filterSoldOutProducts,
    getPhasePortionCount,
    getPickedPhaseProductCount,
    getProductsByPhaseStep,
} from '../../shared/utils/phase';
import { uiBlocker } from '../../../../../library/ui-blocker/ui-blocker';
import { customToastr } from '../../../../../library/toastr/toastr';

const uiBlock = uiBlocker();
const DEFAULT_PRODUCT_INCREMENT = 1;

// Custom hook pro produkty použité v kalkulaci (uložené - savedProducts x vybrané - pickedProducts).
// Vybrané produkty jsou vždy v rámci jednoho kroku, uložené jsou v rámci jedné diety
export const useProducts = (
    products,
    cartItemId,
    dietId,
    phaseId,
    phaseStep,
    loadProductsFromStorage,
    saveProductsToStorage,
    shouldUpdateOrderAfterProductsChange,
    saveProductApiEndpoint,
    phaseStepLimit,
    phaseIds,
    loadAllProductsFromStorage,
    translations,
    savePhaseProductsToStorage,
    loadPhaseProductsFromStorage,
    phases
) => {
    const [pickedProducts, setPickedProducts] = useState([]);
    const [savedProducts, setSavedProducts] = useState(null);
    const [productAvailabilityMap, setProductAvailabilityMap] = useState(null);
    const [filteredCategoryProductsByPhaseStep, setFilteredCategoryProductsByPhaseStep] = useState([]);
    const [pickedPhaseProductsCountMap, setPickedPhaseProductsCountMap] = useState({});
    const [phaseStepProductExceededAvailabilityMap, setPhaseStepProductExceededAvailabilityMap] = useState({});

    useEffect(() => {
        const updatedPickedPhaseProductsCountMap = {};
        phases.forEach((phase) => {
            const updatedPickedPhaseProductsCount = getPickedPhaseProductCount(phase, loadPhaseProductsFromStorage);

            updatedPickedPhaseProductsCountMap[phase.id] = updatedPickedPhaseProductsCount;
        });

        setPickedPhaseProductsCountMap(updatedPickedPhaseProductsCountMap);
    }, [pickedProducts]);

    // Odešle veškeré produkty - při editaci
    const updateOrder = async () => {
        const dietCalculationAutopickButtons = document.getElementsByClassName('autopick-button');
        const dietCalculationFinishOrderButtons = document.getElementsByClassName('diet-calculation-send-offer');

        for (const dietCalculationAutopickButton of dietCalculationAutopickButtons) {
            uiBlock.block(dietCalculationAutopickButton);
        }

        for (const dietCalculationFinishOrderButton of dietCalculationFinishOrderButtons) {
            uiBlock.block(dietCalculationFinishOrderButton);
        }

        const isDietMode = !!dietId;

        const products = loadAllProductsFromStorage(cartItemId, dietId, phaseIds);
        const pickedProducts = getOrdersProductData(isDietMode, products, phaseId);

        const data = {
            cartItemId,
            phaseId,
            dietId,
            pickedProducts,
        };

        try {
            const result = await postData(saveProductApiEndpoint, data);

            if (result.error) {
                customToastr(TOASTR_TYPE_ERROR, result.message);
            } else {
                customToastr(TOASTR_TYPE_SUCCESS, result.message);
            }
        } catch (error) {
            customToastr(TOASTR_TYPE_ERROR, error);
        } finally {
            for (const dietCalculationAutopickButton of dietCalculationAutopickButtons) {
                uiBlock.unblock(dietCalculationAutopickButton);
            }

            for (const dietCalculationFinishOrderButton of dietCalculationFinishOrderButtons) {
                uiBlock.unblock(dietCalculationFinishOrderButton);
            }
        }
    };

    const updatePhaseStepProductAvailabilityMap = (phases) => {
        const updatePhaseStepProductAvailabilityMap = {};

        const filteredProductsWithExceededAvailability = products.filter(
            (product) => productAvailabilityMap[product.id].available < 0
        );

        Object.keys(phases).forEach((phase) => {
            updatePhaseStepProductAvailabilityMap[phase] = {};

            phases[phase]
                ? // Iterace přes všechny kroky fáze
                  Object.keys(phases[phase]).forEach((phaseStep) => {
                      const hasPhaseStepWithExceededAvailabilityProduct =
                          phases[phase][phaseStep] &&
                          phases[phase][phaseStep].some((product) =>
                              filteredProductsWithExceededAvailability.find(
                                  (productWithExceededAvailability) => productWithExceededAvailability.id === product.id
                              )
                          );

                      updatePhaseStepProductAvailabilityMap[phase][phaseStep] =
                          hasPhaseStepWithExceededAvailabilityProduct;
                  })
                : null;
        });

        setPhaseStepProductExceededAvailabilityMap(updatePhaseStepProductAvailabilityMap);
    };

    const removeSoldOutProductsFormPhases = (phases) => {
        const removeProductIds = [];

        // Iterace přes všechny fáze
        Object.keys(phases).forEach((phase) =>
            phases[phase]
                ? // Iterace přes všechny kroky fáze
                  Object.keys(phases[phase]).forEach((phaseStep) => {
                      const hasProducts = !!phases[phase][phaseStep];
                      if (!hasProducts) {
                          return;
                      }
                      phases[phase][phaseStep] = filterSoldOutProducts(
                          phases[phase][phaseStep],
                          removeProductIds,
                          productAvailabilityMap,
                          translations
                      );
                  })
                : null
        );

        // Alespoň jeden produkt smazán => update local storage
        const shouldUpdateLocalStorage = removeProductIds.length > 0;
        if (shouldUpdateLocalStorage) {
            phaseIds.forEach((phaseId) => {
                if (phases[phaseId]) {
                    savePhaseProductsToStorage(cartItemId, dietId, phaseId, phases[phaseId]);
                }
            });
        }
    };

    // Smazat všechny produkty z lokálního uložiště, které byly vyprodány
    useEffect(() => {
        if (!productAvailabilityMap) {
            return;
        }

        const phases = loadAllProductsFromStorage(cartItemId, dietId, phaseIds);
        removeSoldOutProductsFormPhases(phases);
        // uložení všech produktů, které mají přesáhnutou skladovou dostupnost
        updatePhaseStepProductAvailabilityMap(phases);

        setPickedProducts((phases && phases[phaseId] && phases[phaseId][phaseStep]) || []);
    }, [productAvailabilityMap]);

    // TODO: V budoucnu budeme chtít kontrolovat produkty po přechodu mezi kroky.
    // eslint-disable-next-line no-unused-vars
    const removeProductAmountExceedingAvailability = (savedPhaseStepProducts) => {
        const updatedPhaseStepProducts = { ...savedPhaseStepProducts };
        // Promazat z localStorage produkty které přesahují skladovost
        if (productAvailabilityMap && savedPhaseStepProducts) {
            Object.keys(savedPhaseStepProducts).forEach((phaseStep) => {
                savedPhaseStepProducts[phaseStep].forEach((phaseStepProduct, index) => {
                    const remainingProductAvailability =
                        productAvailabilityMap[phaseStepProduct.id].available - phaseStepProduct.amount;
                    const isProductAmountExceedAvailability = remainingProductAvailability < 0;

                    if (isProductAmountExceedAvailability) {
                        delete updatedPhaseStepProducts[phaseStep][index];
                    }
                });
            });
        }
    };

    // Načtení vybraných produktů z localStorage
    useEffect(() => {
        const savedPhaseStepProducts = loadProductsFromStorage(cartItemId, dietId, phaseId, phaseStep);

        // před každým krokem promozat produkty, které mají větší výbět, než skladová dostupnost
        // removeProductAmountExceedingAvailability();
        setPickedProducts((savedPhaseStepProducts && savedPhaseStepProducts[phaseStep]) || []);
    }, [cartItemId, dietId, phaseId, phaseStep]);

    const updateAvailabilityMap = (updatedProducts) => {
        const updatedProductAvailabilityMap = { ...productAvailabilityMap };

        pickedProducts.forEach((product) => {
            updatedProductAvailabilityMap[product.id] = {
                available: updatedProductAvailabilityMap[product.id].available + product.amount,
            };
        });

        updatedProducts.forEach((product) => {
            updatedProductAvailabilityMap[product.id] = {
                available: updatedProductAvailabilityMap[product.id].available - product.amount,
            };
        });

        setProductAvailabilityMap(updatedProductAvailabilityMap);
    };

    // Vytvoření hash mapy dostupnosti produktů
    useEffect(() => {
        if (!products || products.length <= 0) {
            return;
        }

        const newProductAvailabilityMap = indexArray(
            products,
            (product) => product.id,
            (product) => ({ available: product.available })
        );

        const updatedProductAvailabilityMap = { ...newProductAvailabilityMap };

        phaseIds.forEach((phaseId) => {
            const phaseStepProducts = loadProductsFromStorage(cartItemId, dietId, phaseId);
            if (!phaseStepProducts) {
                return;
            }
            Object.keys(phaseStepProducts).forEach((phaseStep) => {
                const availablePhaseStepProducts = phaseStepProducts[phaseStep].filter(
                    (product) => updatedProductAvailabilityMap[product.id]
                );
                availablePhaseStepProducts.forEach((product) => {
                    updatedProductAvailabilityMap[product.id] = {
                        available: updatedProductAvailabilityMap[product.id].available - product.amount,
                    };
                });
            });
        });

        setProductAvailabilityMap(updatedProductAvailabilityMap);
    }, [products]);

    // Uložení produktů v rámci diety
    const saveProducts = (phaseId, phaseStep) => {
        setSavedProducts((prevSavedProducts) => {
            const phaseStepData = { [phaseStep]: [...pickedProducts] };

            if (prevSavedProducts === null) {
                return { [phaseId]: { ...phaseStepData } };
            }

            return { ...prevSavedProducts, [phaseId]: { ...prevSavedProducts[phaseId], ...phaseStepData } };
        });
    };

    // Uživatl klikl na tlačítko - nebo + u produktu
    const onChangeProductAmountByButton = (product, isIncrementing) => {
        const increment = product.increment || DEFAULT_PRODUCT_INCREMENT;
        const amount = isIncrementing
            ? product.amount + increment
            : product.amount - increment < 0
            ? 0
            : product.amount - increment;

        onChangeProductAmount(product.id, amount, product.name);
    };

    // Logika pro upravení počtu výběru produktu (pokud v módu editace a splňujeme limity, tak odesíláme na BE)
    const onChangeProductAmount = (productId, amount, title) => {
        setPickedProducts((prevProducts) => {
            const alreadyPicked = prevProducts.find((product) => product.id === productId);

            let updatedProducts = [...prevProducts];

            if (!alreadyPicked && amount <= 0) {
                return updatedProducts;
            }

            const updatedProductAvailabilityMap = { ...productAvailabilityMap };
            let changedAmount = 0;

            if (alreadyPicked && amount === 0) {
                updatedProducts = updatedProducts.filter((product) => product.id !== productId);
                changedAmount = -alreadyPicked.amount;
            } else if (alreadyPicked && amount > 0) {
                changedAmount = amount - alreadyPicked.amount;

                updatedProducts = updatedProducts.map((product) => {
                    const productWithChangedAmount = product.id === productId;
                    return {
                        ...product,
                        amount: productWithChangedAmount ? amount : product.amount,
                    };
                });
            } else if (!alreadyPicked && amount > 0) {
                updatedProducts.push({ id: productId, amount, title });
                changedAmount = amount;
            }

            const pickedProductsCount = shouldUpdateOrderAfterProductsChange
                ? getMealCount(updatedProducts)
                : undefined;

            // Jen pokud je ve všech fázách completed
            const isAllPhasesComplete = phases.every((phase) => {
                const isActivePhase = phase.id === phaseId;
                return isActivePhase
                    ? pickedProductsCount === phaseStepLimit
                    : pickedPhaseProductsCountMap[phase.id] === getPhasePortionCount(phase);
            });

            const onSaveProductsToStorage =
                shouldUpdateOrderAfterProductsChange && isAllPhasesComplete ? updateOrder : undefined;

            saveProductsToStorage(cartItemId, dietId, phaseId, phaseStep, updatedProducts, onSaveProductsToStorage);

            updatedProductAvailabilityMap[productId] = {
                available: updatedProductAvailabilityMap[productId].available - changedAmount,
            };
            setProductAvailabilityMap(updatedProductAvailabilityMap);

            return updatedProducts;
        });
    };

    const resetPickedProducts = () => {
        setPickedProducts([]);
    };

    const onDecrementProduct = (product) => {
        onChangeProductAmountByButton(product, false);
    };

    const onIncrementProduct = (product) => {
        onChangeProductAmountByButton(product, true);
    };

    const onProductAmountChange = (productId, amount, title) => {
        onChangeProductAmount(productId, amount, title);
    };

    // Vyfiltrujeme produkty pro jednotlý krok
    useEffect(() => {
        setFilteredCategoryProductsByPhaseStep(getProductsByPhaseStep(products, phaseStep));
    }, [phaseStep, products]);

    return {
        pickedProducts,
        savedProducts,
        onDecrementProduct,
        onIncrementProduct,
        onProductAmountChange,
        saveProducts,
        resetPickedProducts,
        setPickedProducts,
        productAvailabilityMap,
        filteredCategoryProductsByPhaseStep,
        updateOrder,
        pickedPhaseProductsCountMap,
        updateAvailabilityMap,
        phaseStepProductExceededAvailabilityMap,
    };
};
